import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import {
  CreateContractPayload,
  QuotationListParams,
  QuotationPayload,
  QuotationRecord,
  QuotationState,
  SelectOptionPayload,
  SelectOptionResult,
  SendQuotationPayload,
  SendDocumentPayload,
  SendDocumentType,
  UploadSignedContractPayload,
} from "models/quotation";

const initialState: QuotationState = {
  list: [],
  total: 0,
  page: 1,
  limit: 10,
  loading: false,
  details: null,
  detailsLoading: false,
  createLoading: false,
  sendLoading: false,
  selectOptionLoading: false,
  contractLoading: false,
  uploadContractLoading: false,
  cancelLoading: false,
  selectedOptionResult: null,
  error: null,
  successMessage: null,
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    // 🔹 List
    getQuotations(state, _action: PayloadAction<QuotationListParams | undefined>) {
      state.loading = true;
      state.error = null;
    },
    getQuotationsSuccess(
      state,
      action: PayloadAction<{ quotations: QuotationRecord[]; total: number; page?: number; limit?: number }>
    ) {
      state.loading = false;
      state.list = action.payload.quotations || [];
      state.total = action.payload.total || 0;
      if (action.payload.page) state.page = action.payload.page;
      if (action.payload.limit) state.limit = action.payload.limit;
    },
    getQuotationsFailed(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // 🔹 Details
    getQuotationById(state, _action: PayloadAction<string>) {
      state.detailsLoading = true;
      state.error = null;
    },
    getQuotationByIdSuccess(state, action: PayloadAction<QuotationRecord>) {
      state.detailsLoading = false;
      state.details = action.payload;
    },
    getQuotationByIdFailed(state, action: PayloadAction<string>) {
      state.detailsLoading = false;
      state.error = action.payload;
    },

    // 🔹 Step 1: Create Quotation
    createQuotation(
      state,
      _action: PayloadAction<{ payload: QuotationPayload; callback?: (record?: any) => void }>
    ) {
      state.createLoading = true;
      state.error = null;
    },
    createQuotationSuccess(state, _action: PayloadAction<any>) {
      state.createLoading = false;
      state.successMessage = "Quotation created successfully";
    },
    createQuotationFailed(state, action: PayloadAction<string>) {
      state.createLoading = false;
      state.error = action.payload;
    },

    // 🔹 Step 2: Update Quotation (DRAFT)
    updateQuotation(
      state,
      _action: PayloadAction<{ payload: QuotationPayload; callback?: (record?: any) => void }>
    ) {
      state.createLoading = true;
      state.error = null;
    },
    updateQuotationSuccess(state, action: PayloadAction<any>) {
      state.createLoading = false;
      state.successMessage = "Quotation updated successfully";
      if (action.payload?._id || action.payload?.id) {
        const id = action.payload._id || action.payload.id;
        state.list = state.list.map((item) => (item._id === id ? { ...item, ...action.payload } : item));
        if (state.details && (state.details._id === id || (state.details as any).id === id)) {
          state.details = { ...state.details, ...action.payload };
        }
      }
    },
    updateQuotationFailed(state, action: PayloadAction<string>) {
      state.createLoading = false;
      state.error = action.payload;
    },

    // 🔹 Step 3: Send Quotation (Locks editing, status => SENT)
    sendQuotation(
      state,
      _action: PayloadAction<{ payload: SendQuotationPayload; callback?: (record?: any) => void }>
    ) {
      state.sendLoading = true;
      state.error = null;
    },
    sendQuotationSuccess(state, action: PayloadAction<{ quotationId: string }>) {
      state.sendLoading = false;
      state.list = state.list.map((item) =>
        item._id === action.payload.quotationId ? { ...item, status: "SENT" as const } : item
      );
      if (state.details && state.details._id === action.payload.quotationId) {
        state.details.status = "SENT";
      }
    },
    sendQuotationFailed(state, action: PayloadAction<string>) {
      state.sendLoading = false;
      state.error = action.payload;
    },

    // 🔹 Send Document (Quotation / Contract via PATCH /quotation/sendDocument)
    sendDocument(
      state,
      _action: PayloadAction<{ payload: SendDocumentPayload; callback?: (record?: any) => void }>
    ) {
      state.sendLoading = true;
      state.error = null;
    },
    sendDocumentSuccess(state, action: PayloadAction<{ id: string; type: SendDocumentType }>) {
      state.sendLoading = false;
      if (action.payload.type === "QUOTATION") {
        state.list = state.list.map((item) =>
          item._id === action.payload.id ? { ...item, status: "SENT" as const } : item
        );
        if (state.details && state.details._id === action.payload.id) {
          state.details.status = "SENT";
        }
      }
    },
    sendDocumentFailed(state, action: PayloadAction<string>) {
      state.sendLoading = false;
      state.error = action.payload;
    },

    // 🔹 Step 4: Select Option (Customer Acceptance, status => ACCEPTED)
    selectOption(
      state,
      _action: PayloadAction<{ payload: SelectOptionPayload; callback?: (result: SelectOptionResult) => void }>
    ) {
      state.selectOptionLoading = true;
      state.error = null;
    },
    selectOptionSuccess(state, action: PayloadAction<SelectOptionResult>) {
      state.selectOptionLoading = false;
      state.selectedOptionResult = action.payload;
      const qId = action.payload.quotation?._id;
      if (qId) {
        state.list = state.list.map((item) =>
          item._id === qId
            ? {
                ...item,
                status: action.payload.quotation.status,
                selectedOptionId: action.payload.quotation.selectedOptionId,
                generatedInvoiceId: action.payload.quotation.generatedInvoiceId,
              }
            : item
        );
      }
      if (state.details && action.payload.quotation) {
        state.details = {
          ...state.details,
          status: action.payload.quotation.status,
          selectedOptionId: action.payload.quotation.selectedOptionId,
          generatedInvoiceId: action.payload.quotation.generatedInvoiceId,
          contract: action.payload.contract || state.details.contract,
        };
      }
    },
    selectOptionFailed(state, action: PayloadAction<string>) {
      state.selectOptionLoading = false;
      state.error = action.payload;
    },

    // 🔹 Step 5: Create Formal Contract (status => PENDING_SIGNATURE)
    createContract(
      state,
      _action: PayloadAction<{ payload: CreateContractPayload; callback?: (contract: any) => void }>
    ) {
      state.contractLoading = true;
      state.error = null;
    },
    createContractSuccess(state, action: PayloadAction<{ quotationId: string; contract: any }>) {
      state.contractLoading = false;
      const { quotationId, contract } = action.payload;
      state.list = state.list.map((item) =>
        item._id === quotationId
          ? { ...item, contractId: contract?._id, contract }
          : item
      );
      if (state.details && state.details._id === quotationId) {
        state.details = {
          ...state.details,
          contractId: contract?._id,
          contract,
        };
      }
    },
    createContractFailed(state, action: PayloadAction<string>) {
      state.contractLoading = false;
      state.error = action.payload;
    },

    // 🔹 Step 6: Upload Signed Contract & Activate (status => ACTIVE)
    uploadSignedContract(
      state,
      _action: PayloadAction<{ payload: UploadSignedContractPayload; callback?: (res: any) => void }>
    ) {
      state.uploadContractLoading = true;
      state.error = null;
    },
    uploadSignedContractSuccess(state, action: PayloadAction<{ contractId: string; contract?: any }>) {
      state.uploadContractLoading = false;
      const { contractId, contract } = action.payload;
      if (state.details?.contract && (state.details.contract._id === contractId || state.details.contractId === contractId)) {
        state.details.contract = {
          ...state.details.contract,
          status: "ACTIVE",
          ...(contract || {}),
        };
      }
      state.list = state.list.map((item) => {
        if (item.contract?._id === contractId || item.contractId === contractId) {
          return {
            ...item,
            contract: {
              ...(item.contract as any),
              status: "ACTIVE",
              ...(contract || {}),
            },
          };
        }
        return item;
      });
    },
    uploadSignedContractFailed(state, action: PayloadAction<string>) {
      state.uploadContractLoading = false;
      state.error = action.payload;
    },

    // 🔹 Cancel
    cancelQuotation(
      state,
      _action: PayloadAction<{ id: string; callback?: () => void }>
    ) {
      state.cancelLoading = true;
      state.error = null;
    },
    cancelQuotationSuccess(state, action: PayloadAction<{ id: string }>) {
      state.cancelLoading = false;
      state.list = state.list.map((item) =>
        item._id === action.payload.id ? { ...item, status: "CANCELLED" as const } : item
      );
      if (state.details && state.details._id === action.payload.id) {
        state.details.status = "CANCELLED";
      }
    },
    cancelQuotationFailed(state, action: PayloadAction<string>) {
      state.cancelLoading = false;
      state.error = action.payload;
    },

    // Reset
    clearSelectedOptionResult(state) {
      state.selectedOptionResult = null;
    },
    clearQuotationDetails(state) {
      state.details = null;
    },
  },
});

export const quotationActions = quotationSlice.actions;

// Selectors
export const selectQuotationList = (state: RootState) => state.quotationModule?.list ?? [];
export const selectQuotationTotal = (state: RootState) => state.quotationModule?.total ?? 0;
export const selectQuotationLoading = (state: RootState) => state.quotationModule?.loading ?? false;
export const selectQuotationPage = (state: RootState) => state.quotationModule?.page ?? 1;
export const selectQuotationLimit = (state: RootState) => state.quotationModule?.limit ?? 10;
export const selectQuotationDetails = (state: RootState) => state.quotationModule?.details ?? null;
export const selectQuotationDetailsLoading = (state: RootState) => state.quotationModule?.detailsLoading ?? false;
export const selectQuotationCreateLoading = (state: RootState) => state.quotationModule?.createLoading ?? false;
export const selectQuotationSendLoading = (state: RootState) => state.quotationModule?.sendLoading ?? false;
export const selectOptionLoading = (state: RootState) => state.quotationModule?.selectOptionLoading ?? false;
export const selectContractLoading = (state: RootState) => state.quotationModule?.contractLoading ?? false;
export const selectUploadContractLoading = (state: RootState) => state.quotationModule?.uploadContractLoading ?? false;
export const selectSelectedOptionResult = (state: RootState) => state.quotationModule?.selectedOptionResult ?? null;
export const selectQuotationCancelLoading = (state: RootState) => state.quotationModule?.cancelLoading ?? false;

export default quotationSlice.reducer;
